import {
  MARKETING_EMAIL_FONT_FAMILY,
  MARKETING_EMAIL_INLINE_BODY_STYLE,
} from './marketingEmailTypography';

const UL_STYLE =
  `margin:0 0 8px;padding-left:24px;list-style-type:disc;font-family:${MARKETING_EMAIL_FONT_FAMILY};font-size:14px;line-height:1.6;color:#1e293b;font-weight:400;`;
const OL_STYLE =
  `margin:0 0 8px;padding-left:24px;list-style-type:decimal;font-family:${MARKETING_EMAIL_FONT_FAMILY};font-size:14px;line-height:1.6;color:#1e293b;font-weight:400;`;
const LI_STYLE = 'margin:0 0 4px;font-weight:400;';
const P_STYLE =
  `margin:0;padding:0;font-family:${MARKETING_EMAIL_FONT_FAMILY};font-size:14px;line-height:1.6;color:#1e293b;font-weight:400;`;
const BODY_WRAPPER_STYLE = `${MARKETING_EMAIL_INLINE_BODY_STYLE};font-weight:400;`;

function stripHtmlText(html: string): string {
  return String(html || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u200b/g, '')
    .trim();
}

/** Strip outer Verdana wrapper so transforms stay idempotent on saved/sent HTML. */
function unwrapEmailBodyWrapper(html: string): string {
  let out = String(html || '').trim();
  for (let i = 0; i < 3; i += 1) {
    const match = out.match(
      /^<div\b[^>]*style="[^"]*font-family:\s*(?:Verdana|Arial)[^"]*"[^>]*>([\s\S]*)<\/div>$/i
    );
    if (!match) break;
    out = match[1].trim();
  }
  return out;
}

/** Convert Quill 2 internal list HTML to real ul/ol (must run before data-* attrs are stripped). */
export function convertQuillListsForEmail(html: string): string {
  let out = String(html || '');
  if (!/<ol\b/i.test(out) && !/<ul\b/i.test(out)) return out;

  out = out.replace(/<span[^>]*class="[^"]*ql-ui[^"]*"[^>]*>[\s\S]*?<\/span>/gi, '');

  return out.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => {
    const items = [...inner.matchAll(/<li([^>]*)>([\s\S]*?)<\/li>/gi)];
    if (!items.length) return `<ol style="${OL_STYLE}">${inner}</ol>`;

    const groups: Array<{ tag: 'ul' | 'ol'; items: string[] }> = [];
    for (const [, attrs, content] of items) {
      const tag: 'ul' | 'ol' = /data-list=["']bullet["']/i.test(attrs) ? 'ul' : 'ol';
      const clean = content.trim();
      const last = groups[groups.length - 1];
      if (!last || last.tag !== tag) groups.push({ tag, items: [clean] });
      else last.items.push(clean);
    }

    return groups
      .map((g) => {
        const style = g.tag === 'ul' ? UL_STYLE : OL_STYLE;
        const lis = g.items.map((item) => `<li style="${LI_STYLE}">${item}</li>`).join('');
        return `<${g.tag} style="${style}">${lis}</${g.tag}>`;
      })
      .join('');
  });
}

/** Preserve Quill paragraph indent as inline padding (email clients ignore ql-indent classes). */
function convertQuillIndentToStyle(html: string): string {
  return String(html || '').replace(
    /<(\w+)\b([^>]*?)\sclass="([^"]*?\bql-indent-(\d+)\b[^"]*)"([^>]*)>/gi,
    (_match, tag, before, _classValue, level, after) => {
      const em = parseInt(level, 10) * 3;
      const indentStyle = `padding-left:${em}em;`;
      const attrs = `${before}${after}`;
      if (/\bstyle="/i.test(attrs)) {
        return `<${tag}${attrs.replace(
          /\bstyle="([^"]*)"/i,
          (_m: string, style: string) => {
            const trimmed = style.trim();
            const sep = trimmed && !trimmed.endsWith(';') ? ';' : '';
            return `style="${trimmed}${sep}${indentStyle}"`;
          }
        )}>`;
      }
      return `<${tag}${before}${after} style="${indentStyle}">`;
    }
  );
}

function convertHeadersToParagraphs(html: string): string {
  return String(html || '').replace(
    /<h([1-6])\b([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (_m, _level, attrs, inner) => `<p${attrs}>${inner}</p>`
  );
}

function shouldUnwrapBoldTag(inner: string): boolean {
  const text = stripHtmlText(inner);
  if (!text) return true;
  if (text.length > 48) return true;
  if (/<br\b/i.test(inner)) return true;
  if (/<p\b/i.test(inner)) return true;
  if (/<li\b/i.test(inner)) return true;
  if (/<(?:ul|ol|div)\b/i.test(inner)) return true;
  return false;
}

function stripPhantomBold(html: string): string {
  let out = String(html || '');
  out = out.replace(/\bfont-weight\s*:\s*(?:700|bold|600|800|900)\s*;?/gi, '');

  // Paragraphs / divs wholly wrapped in strong/b (Quill stuck-bold artifact) — always unwrap.
  out = out.replace(/<p\b([^>]*)>\s*<strong\b[^>]*>([\s\S]*?)<\/strong>\s*<\/p>/gi, '<p$1>$2</p>');
  out = out.replace(/<p\b([^>]*)>\s*<b\b[^>]*>([\s\S]*?)<\/b>\s*<\/p>/gi, '<p$1>$2</p>');
  out = out.replace(/<div\b([^>]*)>\s*<strong\b[^>]*>([\s\S]*?)<\/strong>\s*<\/div>/gi, '<div$1>$2</div>');
  out = out.replace(/<div\b([^>]*)>\s*<b\b[^>]*>([\s\S]*?)<\/b>\s*<\/div>/gi, '<div$1>$2</div>');

  // Outer strong/b wrapping block content.
  out = out.replace(/<strong\b[^>]*>((?:\s*<(?:p|div|br)\b[\s\S]*?)*)<\/strong>/gi, (match, inner) => {
    if (!shouldUnwrapBoldTag(inner)) return match;
    return inner;
  });
  out = out.replace(/<b\b[^>]*>((?:\s*<(?:p|div|br)\b[\s\S]*?)*)<\/b>/gi, (match, inner) => {
    if (!shouldUnwrapBoldTag(inner)) return match;
    return inner;
  });

  for (let pass = 0; pass < 6; pass += 1) {
    let changed = false;
    out = out.replace(/<strong\b[^>]*>([\s\S]*?)<\/strong>/gi, (match, inner) => {
      if (!shouldUnwrapBoldTag(inner)) return match;
      changed = true;
      return inner;
    });
    out = out.replace(/<b\b[^>]*>([\s\S]*?)<\/b>/gi, (match, inner) => {
      if (!shouldUnwrapBoldTag(inner)) return match;
      changed = true;
      return inner;
    });
    if (!changed) break;
  }

  return out;
}

function transformQuillHtmlCore(html: string): string {
  let out = unwrapEmailBodyWrapper(String(html || '').trim());
  if (!out) return out;
  out = convertQuillListsForEmail(out);
  out = convertQuillIndentToStyle(out);
  out = convertHeadersToParagraphs(out);
  out = cleanEditorHtml(out);
  out = stripPhantomBold(out);
  out = addEmailSafeInlineStyles(out);
  return out;
}

/** Normalize editor output for storage (no outer wrapper div). */
export function normalizeQuillBodyHtml(html: string): string {
  return transformQuillHtmlCore(html);
}

/** Strip stuck-bold HTML only — safe to run on every keystroke (no wrapper/styles). */
export function stripMarketingEditorHtml(html: string, removeAllBold = true): string {
  let out = unwrapEmailBodyWrapper(String(html || '').trim());
  if (!out) return out;
  out = convertQuillListsForEmail(out);
  out = convertQuillIndentToStyle(out);
  out = convertHeadersToParagraphs(out);
  out = cleanEditorHtml(out);
  out = stripPhantomBold(out);
  if (removeAllBold) {
    for (let pass = 0; pass < 8; pass += 1) {
      const next = out
        .replace(/<strong\b[^>]*>([\s\S]*?)<\/strong>/gi, '$1')
        .replace(/<b\b[^>]*>([\s\S]*?)<\/b>/gi, '$1');
      if (next === out) break;
      out = next;
    }
  }
  return out;
}

/** Full Quill → email HTML transform (preview + send). Keeps paragraph structure from the editor. */
export function normalizeQuillHtmlForEmail(html: string): string {
  const out = transformQuillHtmlCore(html);
  if (!out) return out;
  if (/^<div style="font-family:\s*(?:Verdana|Arial)/i.test(out)) return out;
  return `<div style="${BODY_WRAPPER_STYLE}">${out}</div>`;
}

/** Prepare HTML for email preview (matches backend sanitizer minus async image upload). */
export function prepareMarketingEmailHtmlForPreview(html: string): string {
  return normalizeQuillHtmlForEmail(html);
}

export function cleanEditorHtml(html: string): string {
  let out = String(html || '');
  out = out.replace(/\sclass="[^"]*"/gi, '');
  out = out.replace(/\scontenteditable="[^"]*"/gi, '');
  out = out.replace(/\sdata-[a-z0-9_-]+="[^"]*"/gi, '');
  return out.trim();
}

function mergeParagraphStyle(existingStyle: string): string {
  const cleaned = String(existingStyle || '')
    .replace(/\bmargin(?:-top|-right|-bottom|-left)?\s*:[^;]+;?/gi, '')
    .replace(/\bpadding\s*:[^;]+;?/gi, '')
    .replace(/\bpadding-(?:top|right|bottom)\s*:[^;]+;?/gi, '')
    .replace(/\bfont-weight\s*:[^;]+;?/gi, '')
    .replace(/\bfont-family\s*:[^;]+;?/gi, '')
    .replace(/\bfont-size\s*:[^;]+;?/gi, '')
    .replace(/\bline-height\s*:[^;]+;?/gi, '')
    .replace(/\bcolor\s*:[^;]+;?/gi, '')
    .trim();
  const base =
    `margin:0;padding-top:0;padding-right:0;padding-bottom:0;font-weight:400;font-family:${MARKETING_EMAIL_FONT_FAMILY};font-size:14px;line-height:1.6;color:#1e293b;`;
  return cleaned ? `${base}${cleaned.endsWith(';') ? cleaned : `${cleaned};`}` : base;
}

export function addEmailSafeInlineStyles(html: string): string {
  let out = String(html || '');
  out = out.replace(/<p\b([^>]*?)style="([^"]*)"([^>]*)>/gi, (_match, before, style, after) => {
    return `<p${before}style="${mergeParagraphStyle(style)}"${after}>`;
  });
  out = out.replace(/<p(?![^>]*style=)([^>]*)>/gi, `<p style="${P_STYLE}"$1>`);
  out = out.replace(/<ul(?![^>]*style=)([^>]*)>/gi, `<ul style="${UL_STYLE}"$1>`);
  out = out.replace(/<ol(?![^>]*style=)([^>]*)>/gi, `<ol style="${OL_STYLE}"$1>`);
  out = out.replace(/<li(?![^>]*style=)([^>]*)>/gi, `<li style="${LI_STYLE}"$1>`);
  out = out.replace(/<strong(?![^>]*style=)([^>]*)>/gi, '<strong style="font-weight:700;"$1>');
  out = out.replace(/<b(?![^>]*style=)([^>]*)>/gi, '<b style="font-weight:700;"$1>');
  return out;
}
